import { inferPrimaryCategoryFromProduct, parseProductTags } from "@/lib/productCategory";

type ProductLike = {
  handle: string;
  title?: string;
  productType?: string;
  tags?: string[] | string | null;
  vendor?: string;
};

/** Slugify to lowercase ASCII hyphenated. */
export const slugify = (s: string): string =>
  s
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/**
 * Resolve a product's display subcategory string. Mirrors the override
 * logic in ProductDetail breadcrumb so URL stays consistent with UI.
 */
export const resolveSubcategory = (product: ProductLike): string => {
  const tags = parseProductTags(product.tags);
  const titleLower = (product.title || "").toLowerCase();
  const productTypeLower = (product.productType || "").toLowerCase();
  const primaryCategory = inferPrimaryCategoryFromProduct({
    title: product.title,
    productType: product.productType,
    tags,
    vendor: product.vendor,
  });

  let subCategory = product.productType || "";

  if (subCategory.toLowerCase() === "bathroom faucets") {
    if (
      titleLower.includes("toilet bowl") ||
      titleLower.includes("toilet rimless") ||
      titleLower.includes("wc bowl") ||
      titleLower.includes("wall mounted toilet") ||
      titleLower.includes("wall-mounted toilet")
    ) {
      subCategory = "Toilets";
    } else if (
      titleLower.includes("bidet") &&
      !titleLower.includes("faucet") &&
      !titleLower.includes("mixer") &&
      !titleLower.includes("tap")
    ) {
      subCategory = "Bidets";
    } else if (titleLower.includes("toilet seat")) {
      subCategory = "Toilet Seats";
    } else if (titleLower.includes("shower tray") || titleLower.includes("brodzik")) {
      subCategory = "Shower Trays";
    } else if (
      titleLower.includes("shower enclosure") ||
      titleLower.includes("shower cabin") ||
      titleLower.includes("walk-in")
    ) {
      subCategory = "Shower Enclosures";
    } else if (
      titleLower.includes("linear drain") ||
      titleLower.includes("drain grid") ||
      titleLower.includes("siphon") ||
      titleLower.includes("odpływ") ||
      titleLower.includes("odplyw") ||
      titleLower.includes("syfon")
    ) {
      subCategory = "Shower Drains";
    } else if (
      titleLower.includes("towel rail") ||
      titleLower.includes("towel holder") ||
      titleLower.includes("towel ring") ||
      titleLower.includes("wieszak")
    ) {
      subCategory = "Towel Rails";
    } else if (titleLower.includes("shelf")) {
      subCategory = "Bathroom Shelves";
    } else if (
      titleLower.includes("control button") ||
      titleLower.includes("flush button") ||
      titleLower.includes("flush plate") ||
      titleLower.includes("przycisk spłukujący") ||
      titleLower.includes("poga slim") ||
      titleLower.includes("skalošanas pogu")
    ) {
      subCategory = "WC Flush Buttons";
    }
  }

  if (
    primaryCategory === "Kitchen" &&
    (titleLower.includes("shelf") ||
      titleLower.includes("hanger") ||
      titleLower.includes("girder") ||
      titleLower.includes("rack"))
  ) {
    subCategory = "Kitchen Accessories";
  }

  if (productTypeLower.includes("hanger") || productTypeLower.includes("girder")) {
    if (titleLower.includes("kitchen")) {
      subCategory = "Kitchen Accessories";
    }
  }

  return subCategory;
};

/** Variant titles that carry no meaning and must not appear in the URL. */
const isMeaninglessVariantTitle = (title?: string | null) => {
  const t = (title || "").trim().toLowerCase();
  return !t || t === "default title" || t === "default";
};

/** Slug for the variant segment, e.g. "Steel Black" -> "steel-black". */
export const variantSlug = (title?: string | null): string =>
  isMeaninglessVariantTitle(title) ? "" : slugify(String(title));

/**
 * Build a product path segment (without language prefix).
 * Returns `/{categorySlug}/{subcategorySlug}/{handle}` (plus an optional
 * `/{variantSlug}` segment) when both category and subcategory can be
 * resolved, otherwise falls back to the legacy `/product/{handle}` form so
 * no product is ever lost.
 */
export const buildProductPath = (
  product: ProductLike,
  opts: { sku?: string | null; variantTitle?: string | null } = {},
): string => {
  const handle = product.handle;
  const vSlug = variantSlug(opts.variantTitle);
  // Only keep ?sku= when we cannot express the variant in the path.
  const skuQuery = !vSlug && opts.sku ? `?sku=${encodeURIComponent(opts.sku)}` : "";

  const tags = parseProductTags(product.tags);
  const primaryCategory = inferPrimaryCategoryFromProduct({
    title: product.title,
    productType: product.productType,
    tags,
    vendor: product.vendor,
  });
  const subCategory = resolveSubcategory(product);

  if (!primaryCategory || !subCategory) {
    return `/product/${handle}${vSlug ? `/${vSlug}` : ""}${skuQuery}`;
  }

  const catSlug = slugify(primaryCategory);
  const subSlug = slugify(subCategory);

  if (!catSlug || !subSlug || catSlug === "product" || subSlug === "product") {
    return `/product/${handle}${vSlug ? `/${vSlug}` : ""}${skuQuery}`;
  }

  return `/${catSlug}/${subSlug}/${handle}${vSlug ? `/${vSlug}` : ""}${skuQuery}`;
};
